import {useEffect, useState} from 'react'
import {callIcon, messageIcon} from '@img/index.js'
import {useInAppCall, CALL_STATE} from '@/hooks/useInAppCall'
import CallOverlay from '@/customComponents/trackDriver/CallOverlay.jsx'
import {isTripCallable} from '@/utils/callable'
import TripChat from '@/customComponents/trackDriver/TripChat.jsx'
import {chatService} from '@/services/chatService'

/**
 * THE TWO WAYS TO REACH A DRIVER — message and call, and everything behind them.
 *
 * ── ⚠️ ONE IMPLEMENTATION, BECAUSE THEY ARE ONE CAPABILITY ────────────────────────
 * This lived inside `DriverCard` (the strip under the tracking map) and was the only place
 * a passenger could reach their driver from. The trip details page — the page they land on
 * from an email, a notification, or the rides list — showed the driver's face, their car and
 * their plate, and no way to contact them at all: a passenger standing at a kerb had to find
 * the tracking screen first.
 *
 * It is extracted rather than copied. The pair carries a call bridge, an unread badge on its
 * own endpoint, a chat thread and a full-screen call overlay; a second copy would be four
 * things to keep in step, and the one that drifts is the one that fails while someone is
 * looking for a car.
 *
 * ── ⚠️ THE CALLER DECIDES WHETHER THERE IS A DRIVER TO REACH ──────────────────────
 * This does NOT re-check `isDriverRevealed`. Both callers already gate on it — they have to,
 * because they show the driver's name and photo beside these buttons — and a component that
 * silently renders nothing is a component whose absence is hard to explain at the call site.
 */
export default function DriverContactButtons({trip, className = ''}) {
    const {state, error, muted, busy, start, hangUp, toggleMute} = useInAppCall(trip?.id)
    const callable = isTripCallable(trip)

    const [chatOpen, setChatOpen] = useState(false)
    const [unread, setUnread] = useState(0)

    /*
     * ⚠️ THE BADGE COMES FROM ITS OWN ENDPOINT, NEVER FROM THE CONVERSATION. Fetching the
     * thread marks the driver's messages read as a side effect, so using it to count them
     * would clear the very number being reported — the badge would drop to zero on its own
     * while nobody had read anything.
     *
     * ⚠️ AND IT IS READ ON OPEN AND ON RETURN TO THE TAB, not on a timer. A count that is
     * a few seconds stale costs nothing; a request every few seconds for the whole of a
     * ride costs a great deal.
     */
    useEffect(() => {
        if (!trip?.id) return undefined
        let cancelled = false
        const refresh = () => {
            // Nothing to count while the thread is open — reading it marks them read.
            if (chatOpen) { setUnread(0); return }
            chatService.unreadCount(trip.id).then((n) => { if (!cancelled) setUnread(n) })
        }
        refresh()

        const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            cancelled = true
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [trip?.id, chatOpen])

    const driver = trip?.driver
    if (!driver) return null

    return (
        <>
            {/* ⚠️ THE MESSAGE THREAD STILL LIVES ON THE TRIP PAGE, so that one stays a link.
                THE CALL DOES NOT: it resolves a masking number and hands it to the phone's
                dialler, and routing a passenger to another screen to press call again is
                two taps and a page load in front of someone standing at a kerb looking for
                their car. There is no second copy to disagree with — both surfaces ask the
                same endpoint, and the backend allocates one session per trip. */}
            {/* ⚠️ `brightness-0` ON BOTH GLYPHS, and it is not a styling preference.
                These icons are drawn for a DARK surface — `callIcon.svg` strokes in
                #EFEFEF — so dropped onto a white disc they came out as a barely visible
                grey smudge, which is exactly how they looked. Collapsing whatever colour an
                icon arrives in to black makes the disc safe for any of them without each
                call site having to know which it was handed. */}
            <div className={`flex shrink-0 items-center gap-3 max-md:gap-2 ${className}`}>
                <button
                    type="button"
                    onClick={() => setChatOpen(true)}
                    aria-label={unread ? `Message your driver, ${unread} unread` : 'Message your driver'}
                    className="relative flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-(--border-color) bg-(--bg-white) max-md:h-9 max-md:w-9"
                >
                    <img src={messageIcon} alt="" className="h-5 w-5 brightness-0 max-md:h-4 max-md:w-4"/>
                    {/* The count itself, not a dot: "3 waiting" and "1 waiting" are
                        different amounts of urgency to someone about to get in a car. */}
                    {unread > 0 && (
                        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-(--text-red) px-1 text-[11px] font-semibold text-white">
                            {unread > 9 ? '9+' : unread}
                        </span>
                    )}
                </button>
                <button
                    type="button"
                    onClick={start}
                    /* ⚠️ DISABLED OUTSIDE THE CALL WINDOW, not left to fail on press. The
                       bridge needs a driver leg to connect to, and outside these statuses
                       the backend has none — so the honest state is an unavailable button
                       rather than one that errors when someone needs it. See
                       `isTripCallable` for why a failed payment hold still counts. */
                    disabled={busy || !callable}
                    aria-label="Call your driver"
                    title={callable ? 'Call your driver' : 'Calling is not available for this trip yet'}
                    className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-(--border-color) bg-(--bg-white) disabled:opacity-50 max-md:h-9 max-md:w-9"
                >
                    <img src={callIcon} alt="" className="h-5 w-5 brightness-0 max-md:h-4 max-md:w-4"/>
                </button>
            </div>

            <TripChat
                tripId={trip.id}
                driverName={driver.name}
                driverPhoto={driver.photo}
                open={chatOpen}
                onClose={() => setChatOpen(false)}
            />

            {/* ⚠️ THE CALL HAS A SCREEN OF ITS OWN, and it has to. Placing the call inside
                the page means none of the dialler's interface comes with it — no ringing,
                no mute, and above all no way to hang up. */}
            {state !== CALL_STATE.idle && (
                <CallOverlay
                    driverName={driver.name}
                    driverPhoto={driver.photo}
                    state={state}
                    error={error}
                    muted={muted}
                    onHangUp={hangUp}
                    onToggleMute={toggleMute}
                    onRetry={start}
                />
            )}
        </>
    )
}

export {DriverContactButtons}
